import {useLivePreview} from 'Application/useCases/useLivePreview';

import {getHrefForLinkType, getRelAttributes, getTargetAttribute} from 'Application/utils/linkFieldUtils';
import {isDefined} from 'Application/utils/typeGuards';

import type {Footer} from 'types/payload-types';

/**
 * A custom hook that provides live preview capabilities for the footer content.
 * This hook connects to the live preview system using the `useLivePreview` utility, enabling real-time updates
 * to the footer data while editing in a CMS or preview environment. It initializes with the provided `footerData`
 * and listens for changes using a unique data key and a specified nesting depth for deep object updates.
 * The hook abstracts away the setup required to watch for footer changes, allowing components to reactively consume
 * updated data without reloading the page.
 *
 * @param footerData The initial `Footer` object containing structured content such as links, legal text, or branding.
 * @returns An object with a reactive `footer` property reflecting the current footer state.
 *
 * @example
 * ```tsx
 * const { footer } = useFooter(initialFooterData);
 * return <FooterComponent data={footer} />;
 * ```
 */
export const useFooter = (footerData: Footer) => {
    const footer = useLivePreview<Footer>({
        dataKey: 'Footer',
        depth: 2,
        initialData: footerData
    });
    let dataProtectionLink = '';
    let dataProtectionLabel = '';
    let dataProtectionRel = '';
    let dataProtectionTarget = '';
    let impressumLink = '';
    let impressumLabel = '';
    let impressumRel = '';
    let impressumTarget = '';
    let machineListLink = '';
    let machineListLabel = '';
    let machineListRel = '';
    let machineListTarget = '';
    const copyrightYear = new Date().getFullYear();

    if (isDefined(footer.copyrightRow.dataProtectionLink)) {
        dataProtectionLink = getHrefForLinkType(footer.copyrightRow.dataProtectionLink);
        dataProtectionLabel = footer.copyrightRow.dataProtectionLink.label;
        dataProtectionRel = getRelAttributes(footer.copyrightRow.dataProtectionLink) ?? '';
        dataProtectionTarget = getTargetAttribute(footer.copyrightRow.dataProtectionLink) ?? '';
    }
    if (isDefined(footer.copyrightRow.imprintLink)) {
        impressumLink = getHrefForLinkType(footer.copyrightRow.imprintLink);
        impressumLabel = footer.copyrightRow.imprintLink.label;
        impressumRel = getRelAttributes(footer.copyrightRow.imprintLink) ?? '';
        impressumTarget = getTargetAttribute(footer.copyrightRow.imprintLink) ?? '';
    }
    if (isDefined(footer.copyrightRow.machineListLink)) {
        machineListLink = getHrefForLinkType(footer.copyrightRow.machineListLink);
        machineListLabel = footer.copyrightRow.machineListLink.label;
        machineListRel = getRelAttributes(footer.copyrightRow.machineListLink) ?? '';
        machineListTarget = getTargetAttribute(footer.copyrightRow.machineListLink) ?? '';
    }

    return {
        contentCol: footer.contentCol,
        contentCol2: footer.contentCol2,
        copyrightText: footer.copyrightRow.copyright,
        copyrightYear,
        dataProtectionLabel,
        dataProtectionLink,
        dataProtectionRel,
        dataProtectionTarget,
        impressumLabel,
        impressumLink,
        impressumRel,
        impressumTarget,
        machineListLabel,
        machineListLink,
        machineListRel,
        machineListTarget
    };
};